<template>
  <article class="sale-summary-card sale-summary-card--activity">
    <header class="sale-summary-card__header sale-summary-card__header--activity">
      <h4>{{ cardText.title }}</h4>
    </header>

    <ul class="sale-summary-activity">
      <li v-if="loading" class="sale-summary-activity__row">
        <div class="sale-summary-activity__body">
          <strong>{{ cardText.loading }}</strong>
        </div>
      </li>

      <li v-else-if="rows.length === 0" class="sale-summary-activity__row">
        <div class="sale-summary-activity__body">
          <strong>{{ cardText.emptyTitle }}</strong>
          <small>{{ emptyDescription }}</small>
        </div>
      </li>

      <li v-else v-for="item in rows" :key="item.id" class="sale-summary-activity__row">
        <span class="sale-summary-activity__icon" :class="`is-${item.type}`">
          <i class="fa" :class="item.icon" aria-hidden="true"></i>
        </span>

        <div class="sale-summary-activity__body">
          <strong>{{ item.title }}</strong>
          <small>{{ item.meta }} · {{ item.actor }}</small>
        </div>
      </li>
    </ul>

    <footer v-if="showFooter" class="sale-summary-activity__footer">
      <button type="button" class="sale-summary-link-btn" @click="$emit('view-history')">
        {{ cardText.viewHistory }}
      </button>
    </footer>
  </article>
</template>

<script>
import customersService from '@/services/customers';
import expenseReceiptsService from '@/services/expenseReceipts';
import invoicesService from '@/services/invoices';
import paymentReceiptsService from '@/services/paymentReceipts';
import salesService from '@/services/sales';
import suppliersService from '@/services/suppliers';
import { extractCollection } from '@/utils/dataNormalization';
import { useDomainText } from '@/composables/text/useDomainText';

const { t } = useDomainText('recentActivity');

export default {
  name: 'RecentActivityCard',
  props: {
    entityId: {
      type: Number,
      default: 0,
    },
    entityType: {
      type: String,
      default: 'customer',
      validator(value) {
        const normalized = String(value || '').trim().toLowerCase();
        return normalized === 'customer'
          || normalized === 'sale'
          || normalized === 'supplier'
          || normalized === 'invoice'
          || normalized === 'paymentreceipt'
          || normalized === 'expensereceipt';
      },
    },
    perPage: {
      type: Number,
      default: 8,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['view-history'],
  data() {
    return {
      loading: false,
      rows: [],
      requestToken: 0,
    };
  },
  computed: {
    isCustomerActivity() {
      return String(this.entityType || '').trim().toLowerCase() === 'customer';
    },
    isSupplierActivity() {
      return String(this.entityType || '').trim().toLowerCase() === 'supplier';
    },
    isCustomerOrSupplierActivity() {
      return this.isCustomerActivity || this.isSupplierActivity;
    },
    isInvoiceActivity() {
      return String(this.entityType || '').trim().toLowerCase() === 'invoice';
    },
    isPaymentReceiptActivity() {
      return String(this.entityType || '').trim().toLowerCase() === 'paymentreceipt';
    },
    isExpenseReceiptActivity() {
      return String(this.entityType || '').trim().toLowerCase() === 'expensereceipt';
    },
    cardText() {
      return {
        title: t('card.title', 'Actividad Reciente'),
        loading: t('card.loading', 'Cargando actividad...'),
        emptyTitle: t('card.emptyTitle', 'Sin actividad reciente'),
        viewHistory: t('card.viewHistory', 'Ver todo el historial'),
      };
    },
    emptyDescription() {
      return this.isCustomerActivity
        ? t('card.emptyCustomerDescription', 'No hay movimientos registrados para este cliente.')
        : (this.isSupplierActivity
          ? t('card.emptySupplierDescription', 'No hay movimientos registrados para este proveedor.')
          : (this.isInvoiceActivity
            ? t('card.emptyInvoiceDescription', 'No hay movimientos registrados para esta factura.')
            : (this.isPaymentReceiptActivity
              ? t('card.emptyPaymentReceiptDescription', 'No hay movimientos registrados para este pago.')
              : (this.isExpenseReceiptActivity
                ? t('card.emptyPaymentReceiptDescription', 'No hay movimientos registrados para este pago.')
                : t('card.emptySaleDescription', 'No hay movimientos registrados para este negocio.')))));
    },
  },
  watch: {
    entityId: {
      immediate: true,
      handler() {
        this.loadRecentActivity();
      },
    },
    perPage() {
      this.loadRecentActivity();
    },
    entityType() {
      this.loadRecentActivity();
    },
  },
  methods: {
    async loadRecentActivity() {
      const entityId = Number(this.entityId || 0);
      if (entityId <= 0) {
        this.rows = [];
        return;
      }

      this.loading = true;
      const token = this.requestToken + 1;
      this.requestToken = token;

      try {
        const safePerPage = Math.max(1, Number(this.perPage || 8));
        const response = await this.resolveActivityService()(entityId, { per_page: safePerPage });
        if (token !== this.requestToken) {
          return;
        }

        this.rows = extractCollection(response)
          .map((row, index) => this.mapActivityRow(row, index))
          .filter((row) => row !== null);
      } catch (error) {
        if (token === this.requestToken) {
          this.rows = [];
        }
      } finally {
        if (token === this.requestToken) {
          this.loading = false;
        }
      }
    },
    resolveActivityService() {
      if (this.isCustomerActivity) {
        return customersService.activity;
      }

      if (this.isSupplierActivity) {
        return suppliersService.activity;
      }

      if (this.isInvoiceActivity) {
        return invoicesService.activity;
      }

      if (this.isPaymentReceiptActivity) {
        return paymentReceiptsService.activity;
      }

      if (this.isExpenseReceiptActivity) {
        return expenseReceiptsService.activity;
      }

      return salesService.activity;
    },
    mapActivityRow(raw, index = 0) {
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const moduleCode = String(raw.module || '').trim().toLowerCase();
      const eventType = String(raw.event_type || '').trim().toLowerCase();
      const happenedAt = String(raw.happened_at || raw.created_at || raw.date_label || '').trim();
      const rawId = String(raw.id || '').trim();

      return {
        id: rawId || `${moduleCode || 'activity'}-${eventType || 'updated'}-${happenedAt || index}`,
        type: this.resolveActivityType(moduleCode, eventType),
        icon: this.resolveActivityIcon(moduleCode, eventType),
        title: String(raw.description_label || raw.description || raw.title || t('row.defaultTitle', 'Actividad registrada')).trim(),
        meta: this.formatActivityMeta(happenedAt),
        actor: String(raw.actor_label || raw.actor_name || t('row.defaultActor', 'Sistema')).trim(),
      };
    },
    resolveActivityType(moduleCode, eventType) {
      if (['deleted', 'canceled'].includes(eventType)) {
        return 'expense';
      }

      if (this.isInvoiceActivity) {
        if (moduleCode === 'document') {
          return 'fee';
        }

        if (moduleCode === 'payment') {
          return 'income';
        }

        if (moduleCode === 'delivery' || moduleCode === 'shipping') {
          return 'income';
        }

        return 'income';
      }

      if (this.isPaymentReceiptActivity) {
        if (moduleCode === 'document') {
          return 'fee';
        }

        if (moduleCode === 'delivery' || moduleCode === 'shipping') {
          return 'income';
        }

        return 'income';
      }

      if (this.isExpenseReceiptActivity) {
        if (moduleCode === 'document') {
          return 'fee';
        }

        if (moduleCode === 'delivery' || moduleCode === 'shipping') {
          return 'income';
        }

        return 'income';
      }

      if (this.isCustomerOrSupplierActivity) {
        if (moduleCode === 'document' || moduleCode === 'person' || moduleCode === 'contributor') {
          return 'fee';
        }

        if (moduleCode === 'expense') {
          return 'expense';
        }

        return 'income';
      }

      if (moduleCode === 'expense') {
        return 'expense';
      }

      if (moduleCode === 'fee') {
        return 'fee';
      }

      return 'income';
    },
    resolveActivityIcon(moduleCode, eventType) {
      if (this.isInvoiceActivity) {
        if (moduleCode === 'document') {
          if (eventType === 'deleted') {
            return 'fa-trash';
          }

          return eventType === 'document_uploaded' || eventType === 'created'
            ? 'fa-file-o'
            : 'fa-file-text-o';
        }

        if (moduleCode === 'delivery' || moduleCode === 'shipping') {
          if (eventType === 'sent') {
            return 'fa-paper-plane-o';
          }

          return 'fa-truck';
        }

        if (moduleCode === 'payment') {
          return eventType === 'paid' ? 'fa-check-circle' : 'fa-credit-card-alt';
        }

        if (eventType === 'deleted' || eventType === 'canceled') {
          return 'fa-trash';
        }

        if (eventType === 'emitted' || eventType === 'created') {
          return 'fa-file-text-o';
        }

        return 'fa-file-o';
      }

      if (this.isExpenseReceiptActivity) {
        if (moduleCode === 'document') {
          if (eventType === 'deleted') {
            return 'fa-trash';
          }

          return eventType === 'document_uploaded' || eventType === 'created'
            ? 'fa-file-o'
            : 'fa-file-text-o';
        }

        if (moduleCode === 'delivery' || moduleCode === 'shipping') {
          if (eventType === 'sent') {
            return 'fa-paper-plane-o';
          }

          return 'fa-truck';
        }

        if (eventType === 'deleted' || eventType === 'canceled') {
          return 'fa-trash';
        }

        if (eventType === 'emitted' || eventType === 'created') {
          return 'fa-file-text-o';
        }

        return 'fa-file-o';
      }

      if (this.isPaymentReceiptActivity) {
        if (moduleCode === 'document') {
          if (eventType === 'deleted') {
            return 'fa-trash';
          }

          return eventType === 'document_uploaded' || eventType === 'created'
            ? 'fa-file-o'
            : 'fa-file-text-o';
        }

        if (moduleCode === 'delivery' || moduleCode === 'shipping') {
          if (eventType === 'sent') {
            return 'fa-paper-plane-o';
          }

          return 'fa-truck';
        }

        if (eventType === 'deleted' || eventType === 'canceled') {
          return 'fa-trash';
        }

        if (eventType === 'emitted' || eventType === 'created') {
          return 'fa-file-text-o';
        }

        return 'fa-file-o';
      }

      if (this.isCustomerOrSupplierActivity) {
        if (moduleCode === 'document') {
          if (eventType === 'deleted') {
            return 'fa-trash';
          }

          return eventType === 'created' ? 'fa-file-o' : 'fa-file-text-o';
        }

        if (moduleCode === 'person') {
          return eventType === 'deleted' ? 'fa-user-times' : 'fa-user-o';
        }

        if (moduleCode === 'contributor') {
          return eventType === 'deleted' ? 'fa-trash' : 'fa-id-card-o';
        }

        if (moduleCode === 'payment') {
          return 'fa-credit-card-alt';
        }

        if (moduleCode === 'purchase_order') {
          return 'fa-file-text-o';
        }

        if (moduleCode === 'expense') {
          return 'fa-minus-circle';
        }

        if (moduleCode === 'fee') {
          return 'fa-user-o';
        }

        if (eventType === 'status_changed') {
          return 'fa-random';
        }

        if (eventType === 'deleted') {
          return 'fa-trash';
        }

        if (eventType === 'created') {
          return 'fa-plus-circle';
        }

        return 'fa-pencil';
      }

      if (moduleCode === 'expense') {
        return 'fa-minus-circle';
      }

      if (moduleCode === 'fee') {
        return 'fa-user-o';
      }

      if (moduleCode === 'payment_plan') {
        return 'fa-calendar-check-o';
      }

      if (eventType === 'deleted') {
        return 'fa-trash';
      }

      return 'fa-credit-card-alt';
    },
    formatActivityMeta(value) {
      const raw = String(value || '').trim();
      if (raw === '') {
        return t('row.recent', 'Reciente');
      }

      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) {
        return raw;
      }

      return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>